import { useState, useRef, useCallback } from 'preact/hooks';
import { createStartGuard } from './startGuard';

export const SOFT_CAP_MS = 50 * 1000;  // 50s — warn before hard stop
export const HARD_CAP_MS = 60 * 1000;  // 1 min hard stop (Whisper OOM guard)
export const LEVEL_BAR_COUNT = 12;

export interface RecorderOptions {
  onSoftCap?: () => void;
  onHardCap?: (chunks: Blob[]) => void;
  onChunk?: (chunk: Blob) => void;
}

const TARGET_SAMPLE_RATE = 16000;
const SILENCE_RMS_THRESHOLD = 0.01; // ~−40 dB; speech is typically 0.05–0.3
const SILENCE_DURATION_MS = 2000;

/** Build a 44-byte RIFF/WAVE header for mono 16-bit PCM at TARGET_SAMPLE_RATE. */
function buildWavHeader(sampleCount: number): Uint8Array {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const dataSize = sampleCount * 2;
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  return new Uint8Array(header);
}

/** True when the runtime supports AudioWorklet — required for this hook. */
export function isAudioWorkletSupported(): boolean {
  if (typeof window === 'undefined') return false;
  const Ctor: typeof AudioContext | undefined =
    (window as Window & { AudioContext?: typeof AudioContext }).AudioContext;
  return !!Ctor && 'audioWorklet' in Ctor.prototype;
}

export function useWavRecorder(options?: RecorderOptions) {
  const [recording, setRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [levelBars, setLevelBars] = useState<number[]>(Array(LEVEL_BAR_COUNT).fill(0));

  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const framesRef = useRef<Int16Array[]>([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const softCapFiredRef = useRef(false);
  const hardCapFiredRef = useRef(false);
  const extCapRef = useRef(0);
  const userStoppedRef = useRef(false);
  const stopResolveRef = useRef<((chunks: Blob[]) => void) | null>(null);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);
  const lastFrameRmsRef = useRef(0);
  const silenceMsRef = useRef(0);
  const silenceWarnedRef = useRef(false);
  // Single-flight latch (#1804): the WAV path has the identical unguarded
  // double-start race as useRecorder (#1801) — a second start() while the first
  // getUserMedia is still in flight would run a full second init and orphan the
  // losing stream (mic stays hot) and AudioContext (leaks). Reachable whenever
  // shouldUseWavRecorder() is true (VITE_USE_WAV_RECORDER / voice-use-wav soak
  // flag) via mic double-click or a rapid hold-to-talk tap. See startGuard.ts.
  const startGuardRef = useRef(createStartGuard());

  const onSoftCapRef = useRef(options?.onSoftCap);
  const onHardCapRef = useRef(options?.onHardCap);
  const onChunkRef = useRef(options?.onChunk);
  onSoftCapRef.current = options?.onSoftCap;
  onHardCapRef.current = options?.onHardCap;
  onChunkRef.current = options?.onChunk;

  const extendCap = useCallback(() => {
    if (extCapRef.current === 0) {
      extCapRef.current = 60 * 1000;
      softCapFiredRef.current = false;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (visibilityCleanupRef.current) {
      visibilityCleanupRef.current();
      visibilityCleanupRef.current = null;
    }
    if (workletRef.current) {
      workletRef.current.port.onmessage = null;
      try { workletRef.current.disconnect(); } catch { /* already disconnected */ }
      workletRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch { /* already disconnected */ }
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.onstatechange = null;
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  /** Concat all Int16 frames into a single audio/wav Blob and resolve. */
  const finalize = useCallback((): Blob[] => {
    const frames = framesRef.current;
    framesRef.current = [];
    let totalSamples = 0;
    for (const f of frames) totalSamples += f.length;
    // 800 samples @ 16kHz = 50ms — anything shorter is a tap artifact,
    // not real speech.  The backend also guards at 2000 bytes, but failing
    // here avoids the WebSocket round-trip and the backend error toast.
    if (totalSamples < 800) return [];
    const samples = new Int16Array(totalSamples);
    let offset = 0;
    for (const f of frames) { samples.set(f, offset); offset += f.length; }
    const header = buildWavHeader(totalSamples);
    // Cast: BlobPart's TypedArray support went through a generics narrowing
    // (Uint8Array<ArrayBufferLike> vs <ArrayBuffer>) in recent TS releases;
    // runtime accepts both.
    const blob = new Blob([header as BlobPart, samples as unknown as BlobPart], { type: 'audio/wav' });
    return [blob];
  }, []);

  const start = useCallback(async (onError?: (msg: string) => void) => {
    // Re-entry guard (#1804): drop a second start() while the first
    // getUserMedia() is still in flight. begin() is synchronous — a concurrent
    // caller no-ops here, BEFORE the first await, so it never runs a second
    // getUserMedia/AudioContext init that would orphan the losing stream (mic
    // stays hot) and AudioContext (leaks). Cleared in finally.
    if (!startGuardRef.current.begin()) return;
    const reportError = (msg: string) => onError?.(msg);
    try {
      if (!isAudioWorkletSupported()) {
        reportError('Microphone unavailable — please update your browser.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: TARGET_SAMPLE_RATE },
      });
      streamRef.current = stream;

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }

      await audioCtx.audioWorklet.addModule('/wav-encoder-worklet.js');
      const workletNode = new AudioWorkletNode(audioCtx, 'wav-encoder');
      workletRef.current = workletNode;

      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;
      source.connect(workletNode);

      framesRef.current = [];
      startTimeRef.current = Date.now();
      softCapFiredRef.current = false;
      hardCapFiredRef.current = false;
      extCapRef.current = 0;
      stopResolveRef.current = null;
      userStoppedRef.current = false;
      silenceMsRef.current = 0;
      silenceWarnedRef.current = false;
      lastFrameRmsRef.current = 0;
      setDurationMs(0);
      setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));

      workletNode.port.onmessage = (e: MessageEvent<Int16Array>) => {
        const frame = e.data;
        if (!frame || !frame.length) return;
        framesRef.current.push(frame);
        // Per-frame RMS for level bars + silence detection. Int16 frames have
        // a fixed dynamic range (-32768..32767); divide to normalise to [-1,1].
        let sumSq = 0;
        for (let i = 0; i < frame.length; i++) {
          const v = frame[i] / 0x8000;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / frame.length);
        lastFrameRmsRef.current = rms;
        // Optional caller-side per-chunk visibility (parity with useRecorder API)
        if (onChunkRef.current) {
          // Wrap the Int16Array as a chunk-shaped Blob so callers don't have to
          // know the underlying type. Cheap; only fires if a callback is set.
          const chunkBlob = new Blob([frame as unknown as BlobPart], { type: 'audio/l16' });
          onChunkRef.current(chunkBlob);
        }
      };

      audioCtx.onstatechange = () => {
        if (audioCtx.state === 'suspended' && recording) {
          console.warn('[useWavRecorder] AudioContext suspended mid-recording, resuming');
          audioCtx.resume().catch(() => {});
        }
      };

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }
      };
      const onPageHide = () => {
        if (audioCtxRef.current && !userStoppedRef.current && !hardCapFiredRef.current) {
          console.warn('[useWavRecorder] pagehide fired — stopping cleanly');
          reportError('Recording paused — your audio so far was captured.');
          // Trigger same path as user-stopped
          userStoppedRef.current = true;
          const chunks = finalize();
          cleanup();
          setRecording(false);
          setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
          const resolve = stopResolveRef.current;
          stopResolveRef.current = null;
          if (resolve) resolve(chunks);
          else if (chunks.length > 0) onHardCapRef.current?.(chunks);
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', onPageHide);
      visibilityCleanupRef.current = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      };

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          if (audioCtxRef.current) {
            console.warn('[useWavRecorder] Audio track ended unexpectedly');
            reportError('Microphone disconnected mid-recording. Please try again.');
            const chunks = finalize();
            cleanup();
            setRecording(false);
            setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
            const resolve = stopResolveRef.current;
            stopResolveRef.current = null;
            if (resolve) resolve(chunks);
            else if (chunks.length > 0) onHardCapRef.current?.(chunks);
          }
        };
      }

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDurationMs(elapsed);

        // Level bars: derive a 12-bar visualisation from the most recent RMS
        // by smoothing decay across bars. Cheaper than running a separate FFT,
        // and matches the perceived loudness the user expects to see.
        const rms = lastFrameRmsRef.current;
        const meterPct = Math.min(100, Math.round(rms * 350));
        setLevelBars((prev) => prev.map((p, i) => {
          const targetForBar = i < (meterPct / (100 / LEVEL_BAR_COUNT)) ? meterPct : Math.max(0, p - 8);
          return targetForBar;
        }));

        // Silence detection: same hysteresis as useRecorder.ts (Phase 4.5),
        // but driven by per-frame RMS rather than analyser bins.
        if (rms < SILENCE_RMS_THRESHOLD) {
          silenceMsRef.current += 100;
        } else {
          silenceMsRef.current = 0;
        }
        if (
          !silenceWarnedRef.current &&
          silenceMsRef.current >= SILENCE_DURATION_MS &&
          elapsed > SILENCE_DURATION_MS
        ) {
          silenceWarnedRef.current = true;
          console.warn(
            `[useWavRecorder] Mic silent for ${silenceMsRef.current}ms ` +
            `(rms=${rms.toFixed(4)}, elapsed=${elapsed}ms) — surfacing toast`,
          );
          reportError(
            'Microphone went silent — check mic permission / Bluetooth routing and try again.',
          );
        }

        const softThreshold = SOFT_CAP_MS + extCapRef.current;
        if (!softCapFiredRef.current && elapsed >= softThreshold) {
          softCapFiredRef.current = true;
          onSoftCapRef.current?.();
        }
        if (!hardCapFiredRef.current && elapsed >= HARD_CAP_MS) {
          hardCapFiredRef.current = true;
          const chunks = finalize();
          cleanup();
          setRecording(false);
          setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
          const resolve = stopResolveRef.current;
          stopResolveRef.current = null;
          if (resolve) resolve(chunks);
          else if (chunks.length > 0) onHardCapRef.current?.(chunks);
        }
      }, 100);

      setRecording(true);
    } catch (err) {
      console.error('[useWavRecorder] start failed:', err);
      reportError('Microphone access denied — allow it in browser settings');
      cleanup();
    } finally {
      // Init window closed (success tail, getUserMedia/worklet reject, OR the
      // unsupported-worklet early return) — release the latch so a normal retry
      // / next recording can re-acquire the mic.
      startGuardRef.current.end();
    }
  }, [cleanup, finalize, recording]);

  const stop = useCallback((): Promise<Blob[]> => {
    return new Promise((resolve) => {
      userStoppedRef.current = true;
      if (!audioCtxRef.current) {
        resolve(framesRef.current.length > 0 ? finalize() : []);
        setRecording(false);
        setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
        return;
      }
      const chunks = finalize();
      cleanup();
      setRecording(false);
      setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
      resolve(chunks);
    });
  }, [cleanup, finalize]);

  // recordingId is minted by useRecorder (the primary path). The WAV path also
  // needs to expose the field so both hooks have a compatible return shape when
  // useRecorderDock selects between them at module load time.
  // WAV recorder mints its own recordingId at start() time.
  const [recordingId, setRecordingId] = useState<string | undefined>(undefined);

  // Wrap start to mint a recordingId each time recording begins.
  const startWithId = useCallback(async (onError?: (msg: string) => void) => {
    const newId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : undefined;
    setRecordingId(newId);
    await start(onError);
  }, [start]);

  return { recording, durationMs, levelBars, extendCap, start: startWithId, stop, recordingId };
}
